import isEmpty from "lodash.isempty";
import { useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useUpdateEffect } from "react-use";
import GradingFilter from "src/features/reporting/components/GradingFilter/GradingFilter";
import { DEFAULT_REPORT_TABLE_FILTERS } from "src/features/reporting/constants";
import { MonthlyRegionReport } from "src/features/reporting/containers";
import MonthlyAreaReport from "src/features/reporting/containers/MonthlyReport/MonthlyAreaReport/MonthlyAreaReport";
import MonthlyStoreReport from "src/features/reporting/containers/MonthlyReport/MonthlyStoreReport/MonthlyStoreReport";
import MonthlyUserReport from "src/features/reporting/containers/MonthlyReport/MonthlyUserReport/MonthlyUserReport";
import ReportTableFilters from "src/features/reporting/containers/ReportTableFilters/ReportTableFilters";
import {
	useObservationFormYears,
	useStores
} from "src/features/reporting/hooks";
import usePreviousObservationFormQuery from "src/hooks/useObservationForm/useFilterFormInstance/useFilterFormInstance";
import usePreviousCompletedObservations from "src/hooks/usePreviousCompletedObservations/usePreviousCompletedObservations";
import usePublishedForm from "src/hooks/usePublishedForm/usePublishedForm";
import useSocialProfile from "src/hooks/useSocialProfile/useSocialProfile";
import { ReportAnswerModal } from "src/modals";
import { checkObservationFormType } from "src/utils/checkObservationFormType";
import setEstateRestrictions from "src/utils/setEstateRestriction";

const EstateReportDetail = () => {
	const [isReportAnswerModalOpen, setIsReportAnswerModalOpen] =
		useState<boolean>(false);

	const params = useParams<{
		formId: string;
	}>();

	const formId = parseInt(params.formId);

	const { data: form } = usePublishedForm(formId);
	const { data: years } = useObservationFormYears(formId);

	const methods = useForm<ReportDetailForm>({
		defaultValues: {
			formId,
			filters: DEFAULT_REPORT_TABLE_FILTERS
		}
	});

	const { control, setValue } = methods;

	const {
		areaId,
		instanceId,
		userId,
		regionId,
		storeId,
		month,
		year,
		showCompliantReport,
		showObservationsPassedThresholdReport,
		showGradingReport,
		calendarMonth
	} = useWatch({
		control,
		name: "filters"
	});

	const { data: socialProfile } = useSocialProfile();
	const { data: stores } = useStores();

	const { refetch: fetchFormInstance } = usePreviousObservationFormQuery({
		formId,
		storeId,
		userId,
		month,
		year,
		complete: true,
		compliant: form?.observation_type === "INDIVIDUAL" ? true : false,
		enabled: Boolean(userId),
		onSuccess: (instances) => {
			if (isEmpty(instances)) return;

			// `all-versions` returns newest-first, so `instances[0]` is the
			// most recent submission for the clicked cell — the modal must open
			// on that one. Picking the last element would land on the OLDEST
			// version and hide newer ones. Mirrors ReportDetail.
			setValue("filters.instanceId", instances[0].id);
		}
	});

	const { data: previousObservations } = usePreviousCompletedObservations(
		{ observationInstanceId: instanceId, year },
		{
			enabled: Boolean(instanceId)
		}
	);

	// See ReportDetail for rationale: `/instances/{id}/previous/` is
	// strictly older than the reference, so include the picked instance
	// itself or the modal renders no answers for it. Filter+append (vs.
	// Set) so the picked id always lands last and the carousel's
	// default activeIndex points at it. `!= null` so id 0 isn't dropped.
	const observations =
		instanceId !== null && instanceId !== undefined && previousObservations
			? [...previousObservations.filter((id) => id !== instanceId), instanceId]
			: previousObservations;

	useUpdateEffect(() => {
		if (!form) return;

		const { observation_type } = form;

		if (observation_type === "INDIVIDUAL") {
			setValue("filters.showCompliantReport", true);
		} else {
			setValue("filters.showObservationsPassedThresholdReport", false);
		}
	}, [form]);

	useEffect(() => {
		setEstateRestrictions(
			socialProfile,
			setValue,
			stores?.results,
			checkObservationFormType(form?.observation_type) === "store"
		);
	}, [socialProfile, stores, form]);

	useUpdateEffect(() => {
		if (!form) return;

		const observationType = form.observation_type;

		if (checkObservationFormType(observationType) === "user" && userId) {
			fetchFormInstance().then(() => setIsReportAnswerModalOpen(true));
		}

		if (checkObservationFormType(observationType) === "store" && storeId) {
			fetchFormInstance().then(() => setIsReportAnswerModalOpen(true));
		}
	}, [storeId, userId, form]);

	const handlecloseReportAnswerModalClick = () => {
		setValue("filters.month", undefined);

		if (!form) return;

		if (checkObservationFormType(form.observation_type) === "user") {
			setValue("filters.userId", undefined);
		}

		if (checkObservationFormType(form.observation_type) === "store") {
			setValue("filters.storeId", undefined);
		}

		setIsReportAnswerModalOpen(false);
	};

	const showMonthlyUserReport =
		regionId &&
		areaId &&
		storeId &&
		checkObservationFormType(form?.observation_type) === "user";

	const showMonthlyStoreReport = form && regionId && areaId && !storeId;

	return (
		<div className="max-w-screen-xl mx-auto py-20 px-4">
			{form && <h1 className="text-2xl font-semibold mb-6">{form.name}</h1>}

			<FormProvider {...methods}>
				{form && observations && (
					<ReportAnswerModal
						isOpen={isReportAnswerModalOpen}
						onClose={handlecloseReportAnswerModalClick}
						observations={observations}
						form={form}
						onExportAllObservationsClick={undefined}
						showGradingReportState={showGradingReport}
					/>
				)}

				{form?.observation_type === "GREAT_CONVERSATION" && <GradingFilter />}

				<hr className="border-foreground/30 my-10" />

				{form?.observation_type === "GREAT_CONVERSATION" && (
					<>
						{showGradingReport ? (
							<h2 className="text-2xl font-semibold mb-8">Grading table</h2>
						) : (
							<h2 className="text-2xl font-semibold mb-8">Full Report</h2>
						)}
					</>
				)}

				{form && years && (
					<ReportTableFilters
						observationType={form.observation_type}
						years={years.results.map(({ year }) => year)}
					/>
				)}

				{form && !regionId && !areaId && !storeId && (
					<MonthlyRegionReport
						formId={formId}
						observationType={form?.observation_type}
						year={year}
						showCompliantReport={showCompliantReport}
						showObservationsPassedThresholdReport={
							showObservationsPassedThresholdReport
						}
						calendarMonth={calendarMonth}
						calendarYear={year}
					/>
				)}

				{form && regionId && !areaId && !storeId && (
					<MonthlyAreaReport
						formId={formId}
						year={year}
						regionId={regionId}
						showCompliantReport={showCompliantReport}
						showObservationsPassedThresholdReport={
							showObservationsPassedThresholdReport
						}
						observationType={form.observation_type}
						calendarMonth={calendarMonth}
						calendarYear={year}
					/>
				)}

				{showMonthlyStoreReport && (
					<MonthlyStoreReport
						formId={formId}
						year={year}
						areaId={areaId}
						showCompliantReport={showCompliantReport}
						showObservationsPassedThresholdReport={
							showObservationsPassedThresholdReport
						}
						observationType={form.observation_type}
						calendarMonth={calendarMonth}
						calendarYear={year}
					/>
				)}

				{showMonthlyUserReport && (
					<MonthlyUserReport
						formId={formId}
						year={year}
						storeId={storeId}
						areaId={areaId}
						compliancy={showCompliantReport}
						observationType={form?.observation_type}
						calendarMonth={calendarMonth}
						calendaryYear={year}
					/>
				)}
			</FormProvider>
		</div>
	);
};

export default EstateReportDetail;
